// usePlayerData.js
// Custom hook to manage player data loading, filtering, and pagination
// Consolidates: players, loading, error, pagination, filtering logic

import { useState, useEffect, useCallback, useRef } from 'react';
import { leaguesAPI } from '../../../../services/apiService';
import { formatApiError } from '../../../../services/errorUtils';

/**
 * Custom hook for managing player data with filtering and pagination
 * Consolidates 9+ state variables into one organized hook
 *
 * @param {string} leagueId - League UUID
 * @param {Object} filters - Filter options (activeTab, positionFilter, searchTerm, showAll, advancedFilters)
 * @param {Object} leagueContext - League context (transactionsEnabled, savedPrices, pricesExist)
 * @param {boolean|null} isCommissionerMode - Authoritative commissioner flag (source of
 *   truth for include_non_40man). When null the caller didn't supply it (unit tests /
 *   other consumers) and the filters flag is used instead.
 * @returns {Object} Player data state and functions
 */
export const usePlayerData = (leagueId, filters = {}, leagueContext = {}, isCommissionerMode = null) => {
  const {
    activeTab = 'hitting',
    positionFilter = 'all',
    searchTerm = '',
    showAll = false,
    advancedFilters = {},
    statsYear = 'current',
    includeNon40Man = false
  } = filters;

  // Determine if we're showing historical (previous year) stats
  const isHistorical = statsYear === 'previous';

  const {
    transactionsEnabled = false,
    savedPrices = {},
    pricesExist = false,
    leagueStatus
  } = leagueContext;

  // ==========================================================================
  // DD-FASEARCH-RACE-0730 — latest-request guard (correctness core).
  // Every load captures a monotonic id; a response whose id is no longer the
  // latest may NEVER write player state. This makes stale / out-of-order
  // responses impossible to render (fixes "McLain under clark" desync and the
  // commissioner false-flag load winning). The AbortController additionally
  // cancels the now-wasted network request.
  // ==========================================================================
  const latestRequestIdRef = useRef(0);
  const abortRef = useRef(null);

  // Begin a new authoritative load: bump the id, abort the prior in-flight
  // request, and return the new id + its AbortSignal. Any load holding an older
  // id must discard its results.
  const beginRequest = useCallback(() => {
    const requestId = ++latestRequestIdRef.current;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* no-op */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    return { requestId, signal: controller.signal };
  }, []);

  const isCurrent = useCallback(
    (requestId) => requestId === latestRequestIdRef.current,
    []
  );

  // Debounce the search term so rapid typing collapses to ONE load instead of
  // one overlapping fetch per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // include_non_40man derived from the AUTHORITATIVE commissioner value at query
  // construction — NOT the render-late setIncludeNon40Man effect
  // (FreeAgentSearch.js). Falls back to the filters flag when the caller doesn't
  // pass isCommissionerMode (unit tests / other consumers).
  const resolveInclude40 = useCallback(
    () => (isCommissionerMode != null ? !!isCommissionerMode : includeNon40Man),
    [isCommissionerMode, includeNon40Man]
  );

  // Core Data State
  const [players, setPlayers] = useState([]);
  const [unfilteredPlayers, setUnfilteredPlayers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Loading State
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination State
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const PLAYERS_PER_PAGE = 100;
  const BATCH_SIZE = 500; // For loading all players

  /**
   * Apply frontend filters to player list
   */
  const applyFrontendFilters = useCallback((playersToFilter) => {
    if (!advancedFilters) return playersToFilter;

    let filtered = [...playersToFilter];

    // Apply stat filters based on active tab — use canonical nested paths
    if (activeTab === 'hitting') {
      // MLB Qualified filter for hitters
      if (advancedFilters.hitterQualified) {
        const minPA = advancedFilters.qualifiedPA || 502;
        filtered = filtered.filter(p => {
          const h = p.stats?.season?.hitting;
          const pa = (h?.at_bats || 0) + (h?.walks || 0) +
                     (h?.hit_by_pitch || 0) + (h?.sacrifice_flies || 0) +
                     (h?.sacrifice_hits || 0);
          return pa >= minPA;
        });
      }

      // At Bats filter
      if (advancedFilters.minAB) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.at_bats || 0) >= parseInt(advancedFilters.minAB));
      }
      if (advancedFilters.maxAB) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.at_bats || 0) <= parseInt(advancedFilters.maxAB));
      }

      // Home Runs filter
      if (advancedFilters.minHR) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.home_runs || 0) >= parseInt(advancedFilters.minHR));
      }
      if (advancedFilters.maxHR) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.home_runs || 0) <= parseInt(advancedFilters.maxHR));
      }

      // Games filter
      if (advancedFilters.minG) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.games_played || 0) >= parseInt(advancedFilters.minG));
      }
      if (advancedFilters.maxG) {
        filtered = filtered.filter(p => (p.stats?.season?.hitting?.games_played || 0) <= parseInt(advancedFilters.maxG));
      }

    } else if (activeTab === 'pitching') {
      // MLB Qualified filter for pitchers
      if (advancedFilters.pitcherQualified) {
        const minIP = advancedFilters.qualifiedIP || 162;
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.innings_pitched || 0) >= minIP);
      }

      // Strikeouts filter
      if (advancedFilters.minK) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.strikeouts_pitched || 0) >= parseInt(advancedFilters.minK));
      }
      if (advancedFilters.maxK) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.strikeouts_pitched || 0) <= parseInt(advancedFilters.maxK));
      }

      // Games Started filter
      if (advancedFilters.minGS) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.games_started || 0) >= parseInt(advancedFilters.minGS));
      }
      if (advancedFilters.maxGS) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.games_started || 0) <= parseInt(advancedFilters.maxGS));
      }

      // Innings Pitched filter
      if (advancedFilters.minIP) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.innings_pitched || 0) >= parseFloat(advancedFilters.minIP));
      }
      if (advancedFilters.maxIP) {
        filtered = filtered.filter(p => (p.stats?.season?.pitching?.innings_pitched || 0) <= parseFloat(advancedFilters.maxIP));
      }
    }

    // Price filter (both tabs)
    const filterIsOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
    if (advancedFilters.minPrice) {
      filtered = filtered.filter(p => {
        const contract = filterIsOffseason && p.projected_contract
            ? p.projected_contract
            : p.league_context?.financial;
        const price = savedPrices[p.ids?.mlb] ?? contract?.baseline_price;
        return price >= parseInt(advancedFilters.minPrice);
      });
    }
    if (advancedFilters.maxPrice) {
      filtered = filtered.filter(p => {
        const contract = filterIsOffseason && p.projected_contract
            ? p.projected_contract
            : p.league_context?.financial;
        const price = savedPrices[p.ids?.mlb] ?? contract?.baseline_price;
        return price <= parseInt(advancedFilters.maxPrice);
      });
    }

    return filtered;
  }, [advancedFilters, activeTab, savedPrices, leagueStatus]);

  /**
   * Load ALL players in batches for virtual scrolling
   * Used when showAll = true to load entire dataset
   */
  const loadAllPlayers = useCallback(async () => {
    // 9o.6.e Slice 2 Commit 7-rest: pending-custom-input guard.
    // When dateRange='custom' but either date is empty, the user is mid-input;
    // firing the request would produce a backend 422. Skip until both dates present.
    const dateRange = advancedFilters?.dateRange ?? 'season';
    if (dateRange === 'custom') {
      if (!advancedFilters?.customStartDate || !advancedFilters?.customEndDate) {
        return;
      }
    }

    // DD-FASEARCH-RACE-0730: pin an immutable query descriptor at kickoff. The whole
    // paginated loop uses this object — never a ref that can mutate mid-run — so every
    // page carries identical params (kills mixed false/true include_non_40man pages).
    const { requestId, signal } = beginRequest();
    const query = Object.freeze({
      search: (debouncedSearch || '').trim(),
      include_non_40man: resolveInclude40(),
      activeTab,
      positionFilter,
      showAll,
      isHistorical,
      dateRange,
      customStartDate: dateRange === 'custom' ? advancedFilters.customStartDate : null,
      customEndDate: dateRange === 'custom' ? advancedFilters.customEndDate : null,
    });

    setLoading(true);
    setPlayers([]);
    setUnfilteredPlayers([]);
    setPage(0);
    setHasMore(true);
    setAllDataLoaded(false);
    setError('');

    let allPlayers = [];
    let currentOffset = 0;
    let hasMoreData = true;

    try {
      // Loop until we've loaded all players
      while (hasMoreData) {
        const apiFilters = {
          position: query.activeTab,
          limit: BATCH_SIZE,
          offset: currentOffset,
          show_all: query.showAll,          // getFreeAgentsCanonical converts to available_only
          is_historical: query.isHistorical,
          include_non_40man: query.include_non_40man,  // pinned at kickoff (authoritative)
          dateRange: query.dateRange,
          customStartDate: query.customStartDate,
          customEndDate: query.customEndDate,
        };

        // Handle specific position filters (including CI and MI)
        if (query.positionFilter !== 'all') {
          apiFilters.position = query.positionFilter;
        }

        // Add search term if present
        if (query.search) {
          apiFilters.search = query.search;
        }

        // Sort parameters
        const sortParams = {
          sort_by: query.activeTab === 'hitting' ? 'at_bats' : 'games_started',
          sort_order: 'desc'
        };

        const response = await leaguesAPI.getFreeAgentsCanonical(
          leagueId,
          { ...apiFilters, ...sortParams },
          { signal }
        );

        // GUARD: a newer load superseded this one — discard, write nothing.
        if (!isCurrent(requestId)) return;

        if (response.success) {
          const batchPlayers = response.players || [];

          // Add pricing data to players
          const batchIsOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
          let playersWithPrices = batchPlayers.map(player => {
            const playerId = player.ids?.mlb;
            const contract = batchIsOffseason && player.projected_contract
                ? player.projected_contract
                : player.league_context?.financial;
            const price = savedPrices[playerId] ?? contract?.baseline_price;

            return {
              ...player,
              price: price,
              display_price: price,
              display_salary: contract?.contract_salary
            };
          });

          // Frontend filtering for MI/CI positions
          if (query.positionFilter === 'MI') {
            playersWithPrices = playersWithPrices.filter(p =>
              p.info?.position === '2B' || p.info?.position === 'SS'
            );
          } else if (query.positionFilter === 'CI') {
            playersWithPrices = playersWithPrices.filter(p =>
              p.info?.position === '1B' || p.info?.position === '3B'
            );
          } else if (query.positionFilter === 'OF') {
            playersWithPrices = playersWithPrices.filter(p =>
              ['OF', 'LF', 'CF', 'RF'].includes(p.info?.position)
            );
          } else if (query.positionFilter !== 'all') {
            playersWithPrices = playersWithPrices.filter(p => p.info?.position === query.positionFilter);
          }

          // Accumulate players
          allPlayers = [...allPlayers, ...playersWithPrices];

          // Check if there are more pages
          hasMoreData = response.has_more || false;
          currentOffset += BATCH_SIZE;

          // GUARD before every state write: only the latest query may paint.
          if (!isCurrent(requestId)) return;
          setUnfilteredPlayers(allPlayers);
          const filteredPlayers = applyFrontendFilters(allPlayers);
          setPlayers(filteredPlayers);
          setTotalCount(filteredPlayers.length);
        } else {
          hasMoreData = false;
        }
      }

      if (!isCurrent(requestId)) return;
      setAllDataLoaded(true);
      setHasMore(false);

    } catch (err) {
      // Aborted or superseded loads must not surface an error or clobber state.
      if (signal.aborted || !isCurrent(requestId)) return;
      console.error('❌ Error loading all players:', err);
      setError(formatApiError(err, 'Failed to load all players'));
    } finally {
      // Only the latest load owns the loading flag.
      if (isCurrent(requestId)) setLoading(false);
    }
  }, [leagueId, debouncedSearch, activeTab, positionFilter, showAll, savedPrices, applyFrontendFilters, isHistorical, resolveInclude40, beginRequest, isCurrent, leagueStatus, advancedFilters?.dateRange, advancedFilters?.customStartDate, advancedFilters?.customEndDate]);

  /**
   * Load players from API with current filters
   */
  const loadPlayers = useCallback(async (pageNum = 0, append = false) => {
    // ALWAYS load ALL players in batches (no pagination anymore)
    if (pageNum === 0 && !append) {
      return loadAllPlayers();
    }

    // 9o.6.e Slice 2: pending-custom-input guard (see loadAllPlayers above).
    const dateRange = advancedFilters?.dateRange ?? 'season';
    if (dateRange === 'custom') {
      if (!advancedFilters?.customStartDate || !advancedFilters?.customEndDate) {
        return;
      }
    }

    // Append/pagination path is a CONTINUATION of the active query — it captures the
    // current latest id (does not start a new one) and bails if a newer load began.
    const requestId = latestRequestIdRef.current;
    const signal = abortRef.current?.signal;
    const query = Object.freeze({
      search: (debouncedSearch || '').trim(),
      include_non_40man: resolveInclude40(),
      activeTab,
      positionFilter,
      showAll,
      isHistorical,
      dateRange,
      customStartDate: dateRange === 'custom' ? advancedFilters.customStartDate : null,
      customEndDate: dateRange === 'custom' ? advancedFilters.customEndDate : null,
    });

    setBackgroundLoading(true);
    setError('');

    try {
      const apiFilters = {
        position: query.activeTab,
        limit: PLAYERS_PER_PAGE,
        offset: pageNum * PLAYERS_PER_PAGE,
        is_historical: query.isHistorical,
        show_all: query.showAll,
        include_non_40man: query.include_non_40man,
        dateRange: query.dateRange,
        customStartDate: query.customStartDate,
        customEndDate: query.customEndDate,
      };

      // Handle specific position filters (including CI and MI — backend handles expansion)
      if (query.positionFilter !== 'all') {
        apiFilters.position = query.positionFilter;
      }

      // Add search term if present
      if (query.search) {
        apiFilters.search = query.search;
      }

      // Sort parameters
      const sortParams = {
        sort_by: query.activeTab === 'hitting' ? 'at_bats' : 'games_started',
        sort_order: 'desc'
      };

      const response = await leaguesAPI.getFreeAgentsCanonical(
        leagueId,
        { ...apiFilters, ...sortParams },
        { signal }
      );

      if (!isCurrent(requestId)) return;

      if (response.success) {
        // Add pricing data to players
        const loadIsOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
        let playersWithPrices = (response.players || []).map(player => {
          const playerId = player.ids?.mlb;
          const contract = loadIsOffseason && player.projected_contract
              ? player.projected_contract
              : player.league_context?.financial;
          const price = savedPrices[playerId] ?? contract?.baseline_price;

          return {
            ...player,
            price: price,
            display_price: price,
            display_salary: contract?.contract_salary
          };
        });

        // Frontend filtering for MI/CI positions
        if (query.positionFilter === 'MI') {
          playersWithPrices = playersWithPrices.filter(p =>
            p.info?.position === '2B' || p.info?.position === 'SS'
          );
        } else if (query.positionFilter === 'CI') {
          playersWithPrices = playersWithPrices.filter(p =>
            p.info?.position === '1B' || p.info?.position === '3B'
          );
        } else if (query.positionFilter !== 'all') {
          playersWithPrices = playersWithPrices.filter(p => p.info?.position === query.positionFilter);
        }

        if (!isCurrent(requestId)) return;
        // Append path (pageNum > 0) accumulates onto the existing list.
        setUnfilteredPlayers(prev => [...prev, ...playersWithPrices]);
        setPlayers(prev => {
          const merged = [...prev, ...applyFrontendFilters(playersWithPrices)];
          return merged;
        });
        setHasMore(response.has_more || false);
        setPage(pageNum);

      } else {
        setError(response.message || `Failed to load ${showAll ? 'all players' : 'free agents'}`);
      }
    } catch (err) {
      if (signal?.aborted || !isCurrent(requestId)) return;
      console.error(`Error loading ${showAll ? 'all players' : 'free agents'}:`, err);
      setError(formatApiError(err, `Failed to load ${showAll ? 'all players' : 'free agents'}`));
    } finally {
      if (isCurrent(requestId)) setBackgroundLoading(false);
    }
  }, [leagueId, debouncedSearch, activeTab, positionFilter, showAll, savedPrices, applyFrontendFilters, loadAllPlayers, isHistorical, resolveInclude40, isCurrent, leagueStatus, advancedFilters?.dateRange, advancedFilters?.customStartDate, advancedFilters?.customEndDate]);

  /**
   * Load next page of results
   */
  const loadMore = useCallback(() => {
    if (!backgroundLoading && hasMore) {
      loadPlayers(page + 1, true);
    }
  }, [backgroundLoading, hasMore, page, loadPlayers]);

  /**
   * Load players initially when context is ready.
   * DD-FASEARCH-RACE-0730: the cleanup invalidates any in-flight load (bump id +
   * abort) on every dep change, so a superseded load's response can never write
   * state. searchTerm is debounced; isCommissionerMode is a dep so a commissioner
   * toggle refires with the correct flag.
   */
  useEffect(() => {
    if (leagueContext.isReady && (transactionsEnabled || leagueContext.browseMode)) {
      loadPlayers(0, false);
    }
    return () => {
      latestRequestIdRef.current += 1;
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* no-op */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueContext.isReady, transactionsEnabled, leagueContext.browseMode, activeTab, positionFilter, showAll, debouncedSearch, isHistorical, includeNon40Man, isCommissionerMode, advancedFilters?.dateRange, advancedFilters?.customStartDate, advancedFilters?.customEndDate]);

  /**
   * Re-apply filters when advanced filters change
   */
  useEffect(() => {
    if (unfilteredPlayers.length > 0) {
      const filtered = applyFrontendFilters(unfilteredPlayers);
      setPlayers(filtered);
      setTotalCount(filtered.length);
    }
  }, [advancedFilters, unfilteredPlayers, applyFrontendFilters]);

  return {
    // Data
    players,
    setPlayers,
    totalCount,
    setTotalCount,

    // Loading states
    loading,
    backgroundLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,

    // Pagination
    page,
    hasMore,
    allDataLoaded,

    // Functions
    loadPlayers,
    loadMore
  };
};
